import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Loader2, LogOut, ArrowLeft } from 'lucide-react';
import { useShift } from '../../../context/ShiftContext';
import { useAuth } from '../../../context/AuthContext';
import toast from 'react-hot-toast';

export default function ShiftGate() {
  const navigate = useNavigate();
  const { hasActiveShift, loading, shiftEnabled, startShift } = useShift();
  const { user, logout } = useAuth();
  const [openingCash, setOpeningCash] = useState('');
  const [starting, setStarting] = useState(false);

  // Don't render gate if shift management is disabled, shift is active, or still loading
  if (!shiftEnabled || loading || hasActiveShift) return null;

  const handleStart = async () => {
    setStarting(true);
    try {
      const cash = openingCash !== '' ? parseFloat(openingCash) : 0;
      await startShift(cash);
      toast.success('Shift started successfully!');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to start shift';
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
            <Clock size={32} className="text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Start Your Shift</h2>
          <div className="text-sm text-slate-500">
            <p className="font-medium text-slate-700">{user?.name || 'User'}</p>
            <p>{dateStr}</p>
            <p>{timeStr}</p>
          </div>
        </div>

        {/* Opening Cash Input */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Opening Cash in Drawer
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-lg">
              ₹
            </span>
            <input
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleStart();
              }}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full pl-10 pr-4 py-3.5 border-2 border-slate-300 rounded-xl text-lg font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center"
              autoFocus
            />
          </div>
          <p className="text-xs text-slate-400 mt-1.5">
            Enter the cash amount currently in the register drawer
          </p>
        </div>

        {/* Start Button */}
        <button
          onClick={handleStart}
          disabled={starting}
          className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {starting ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Starting...
            </>
          ) : (
            'Start Shift'
          )}
        </button>

        {/* Navigation Options */}
        <div className="flex items-center justify-center gap-4 pt-2 border-t border-slate-200">
          {user?.role === 'both' && (
            <button
              onClick={() => navigate('/select-role')}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeft size={14} />
              Switch Portal
            </button>
          )}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 transition-colors"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
